import { useRef } from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography, { TypographyProps } from "@mui/material/Typography";
import { DreamSearchViewerResult } from "@portal/app/_common/types/dreamSearch";
import { useDreamSearchImpression } from "../../useDreamSearchImpression";
import { MixpanelEventName } from "@portal/app/_common/types/mixpanel";
import { DreamSearchResultPaymentInfo } from "./DreamSearchResultPaymentInfo";
import NextLink from "next/link";
import { faShareFromSquare } from "@fortawesome/pro-solid-svg-icons/faShareFromSquare";
import Button from "@mui/material/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Skeleton from "@mui/material/Skeleton";
import { useDreamSearchResultNavigation } from "./useDreamSearchResultNavigation";

export const DreamSearchResult = ({
  i,
  title,
  eventName,
  item,
  logoUrl,
  logoBackgroundColor,
  titleChips,
  content,
}: {
  i: number;
  title: React.ReactNode;
  eventName: MixpanelEventName | null;
  logoUrl?: string | null;
  logoBackgroundColor?: string | null;
  titleChips?: React.ReactNode;
  content?: React.ReactNode;
  item?: DreamSearchViewerResult;
}) => {
  const ref = useRef<HTMLAnchorElement>(null);
  useDreamSearchImpression(ref, item, i);
  const { href, isClickout, handleClick, canRequestInfo } = useDreamSearchResultNavigation(
    item,
    eventName
  );

  return (
    <ResultContainer
      ref={ref}
      prefetch={false}
      href={href}
      target={isClickout ? "_blank" : "_self"}
      onClick={handleClick}
      data-testid={item ? `list-item-${item.id}` : "list-item-skeleton"}
    >
      <UpperContainer $logoBackgroundColor={logoBackgroundColor}>
        {!!logoUrl && (
          <LogoContainer>
            <Logo>
              <ProviderLogo src={logoUrl} alt="logo" />
            </Logo>
          </LogoContainer>
        )}
        <UpperInfoContainer>
          {!!titleChips && <TitleChips>{titleChips}</TitleChips>}
          <TitleContainer>
            {/* Keeps the `new_h3` look but renders a paragraph: the card's heading is the program
                name in `DreamSearchResultProgramName`, which is what these pages target. */}
            <Title
              variant="new_h3"
              component="p"
              $logoBackgroundColor={logoBackgroundColor}
              $titleLength={typeof title === "string" ? title.length : 0}
              data-testid="result-title"
            >
              {title}
            </Title>
          </TitleContainer>
        </UpperInfoContainer>
      </UpperContainer>
      <ContentContainer className="dim_on_hover">{content}</ContentContainer>
      <FooterContainer className="dim_on_hover">
        <ActionBoxLeft>
          {item ? (
            <DreamSearchResultPaymentInfo item={item} />
          ) : (
            <Skeleton variant="text" height={40} width="90%" />
          )}
        </ActionBoxLeft>
        {(!item || canRequestInfo) && (
          <ActionBoxRight>
            {item ? (
              <Button
                component="span"
                role={undefined}
                tabIndex={-1}
                size="small"
                variant="onDark"
                data-testid={`id-${item.program.id}`}
              >
                {item.program.customCtaCopy || "Request info"}
                {isClickout ? (
                  <Box pl={0.5}>
                    <FontAwesomeIcon icon={faShareFromSquare} />
                  </Box>
                ) : null}
              </Button>
            ) : (
              <Skeleton variant="rounded" height={40} width={115} />
            )}
          </ActionBoxRight>
        )}
      </FooterContainer>
    </ResultContainer>
  );
};

const ResultContainer = styled(NextLink)`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  border-radius: 1rem;
  text-decoration: none;

  &:hover {
    box-shadow: 0.1rem 0.1rem 0.2rem #e8e5de;

    .dim_on_hover {
      background: #f8f8f9;
    }
  }
`;

const UpperContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$logoBackgroundColor",
})<{
  $logoBackgroundColor: string | null | undefined;
}>`
  position: relative;
  border-radius: 1rem 1rem 0 0;
  height: 5.5rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-bottom: none;
  ${({ theme, $logoBackgroundColor }) => ({
    backgroundColor: $logoBackgroundColor ? $logoBackgroundColor : theme.palette.dream.grayLight,
  })}
`;

const LOGO_WIDTH = 35;
const UpperInfoContainer = styled(Box)`
  display: flex;
  flex-direction: column;

  height: 100%;
  width: ${100 - LOGO_WIDTH + 10}%;
  padding: 0.75rem 0 0.75rem 1rem;
`;

const TitleChips = styled(Box)`
  display: flex;
  margin-bottom: 0.25rem;
`;

const TitleContainer = styled(Box)`
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  justify-content: start;
  overflow: hidden;
`;

const LONG_TITLE_THRESHOLD = 60;

const Title = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$logoBackgroundColor" && prop !== "$titleLength",
})<TypographyProps & { $logoBackgroundColor: string | null | undefined; $titleLength: number }>`
  display: flex;
  align-items: center;
  flex-grow: 1;
  font-family: inherit;
  text-transform: none;

  margin: 0;
  background-color: inherit;
  color: ${({ theme, $logoBackgroundColor }) =>
    theme.palette.getContrastText($logoBackgroundColor ?? theme.palette.dream.grayLight)};
  background-clip: text;
  -webkit-background-clip: text;
  ${({ $titleLength }) =>
    $titleLength > LONG_TITLE_THRESHOLD &&
    `font-size: 0.85rem;
     line-height: 1.2;`}
`;

const LogoContainer = styled(Box)`
  position: absolute;
  height: 100%;
  width: 100%;
  mask-image: linear-gradient(
    270deg,
    rgba(0, 0, 0, 0.8) 0%,
    rgba(0, 0, 0, 0.5) ${LOGO_WIDTH * 0.6}%,
    transparent ${LOGO_WIDTH}%
  );
  border-top-right-radius: 1rem;
`;

const Logo = styled(Box)`
  position: relative;
  height: 120%;
  transform: translateY(-8%);
  width: ${LOGO_WIDTH}%;
  margin-left: ${100 - LOGO_WIDTH}%;
`;

const ProviderLogo = styled("img")`
  position: absolute;
  height: 100%;
  width: 100%;
  inset: 0px;
  object-fit: cover;
  object-position: left center;
  color: transparent;
`;

const ContentContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  padding: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-top: none;
  border-bottom: none;
  background: white;

  & > :not(:last-child) {
    margin-bottom: 0.5rem;
  }
`;

const FooterContainer = styled(Box)`
  height: 5rem;
  padding: 1rem;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0 0 1rem 1rem;
  min-height: 60px;
  background: white;

  display: flex;
  justify-content: flex-end;
  align-items: center;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    height: 5.5rem;
  }
`;

const ActionBoxLeft = styled(Box)`
  flex: 1 1;
`;
const ActionBoxRight = styled(Box)`
  .MuiButtonBase-root {
    font-weight: bold;
  }
`;
